import { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { isValidEmail } from 'utils-ts/email-helpers';

interface EmailChipInputProps {
  emails: string[];
  onChange: (emails: string[]) => void;
  label?: string;
  disabled?: boolean;
}

/**
 * A reusable component for inputting multiple email addresses as chips.
 */
const EmailChipInput = ({
  emails,
  onChange,
  label = "To - press 'Enter' or 'Tab' to add emails",
  disabled = false
}: EmailChipInputProps) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === 'Enter' || event.key === 'Tab') && inputValue) {
      event.preventDefault();
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !emails.includes(trimmedValue)) {
        onChange([...emails, trimmedValue]);
      }
      setInputValue('');
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={emails}
      inputValue={inputValue}
      onInputChange={(_, newValue) => setInputValue(newValue)}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      renderInput={(params) => <TextField {...params} label={label} required onKeyDown={handleKeyDown} />}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            color={isValidEmail(option) ? 'primary' : 'error'}
            title={isValidEmail(option) ? option : 'Invalid email format'}
          />
        ))
      }
      disabled={disabled}
    />
  );
};

export default EmailChipInput;
